import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { BookOpen, Route } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { PageHeader, Spinner, EmptyState } from "../../components/common";
import { ManualReader } from "../../components/manual/ManualReader";
import { AppFlowViewer } from "../../components/appflow/AppFlowViewer";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/** Read-only "Help & User Manual" for Municipal Admins and Supervisors (role-filtered guides) + Phase 222 App Flow tab. */
export default function HelpManual() {
  const { code } = useParams();
  const { auth } = useAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const tab = params.get("tab") === "appflow" ? "appflow" : "manual";
  const setTab = (v) => { const p = new URLSearchParams(params); if (v === "appflow") p.set("tab", "appflow"); else p.delete("tab"); setParams(p, { replace: true }); };

  const load = () => {
    setError(null);
    api.get(`/t/${code}/manual`).then((r) => setData(r.data))
      .catch((e) => { setError(errMsg(e)); toast.error(errMsg(e)); });
  };
  useEffect(load, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  const role = auth?.user?.role;
  const initialGuide = role === "supervisor" ? "supervisor" : "municipal_admin";

  return (
    <div className="space-y-4">
      <PageHeader title="Help & User Manual" subtitle="Step-by-step guides for your console and the teams you work with — updated automatically with every release">
        {data?.version && (
          <Badge variant="outline" className="text-xs" data-testid="help-version-badge">
            Manual v{data.version.number} · {data.version.release_tag}
          </Badge>
        )}
      </PageHeader>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="manual" data-testid="help-tab-manual"><BookOpen className="h-4 w-4 mr-1.5" /> User Manual</TabsTrigger>
          <TabsTrigger value="appflow" data-testid="help-tab-appflow"><Route className="h-4 w-4 mr-1.5" /> App Flow</TabsTrigger>
        </TabsList>
        <TabsContent value="manual" className="mt-4">
          {error ? (
            <EmptyState title="Could not load the manual" subtitle={error}
              action={<Button variant="outline" size="sm" onClick={load} data-testid="help-retry">Retry</Button>} />
          ) : !data ? <Spinner label="Loading manual..." /> : (
            <ManualReader
              guides={data.guides}
              versionMeta={data.version}
              searchPath={`/t/${code}/manual/search`}
              exportPath={`/t/${code}/manual/export`}
              initialGuide={initialGuide}
            />
          )}
        </TabsContent>
        <TabsContent value="appflow" className="mt-4">
          <AppFlowViewer fetchPath={`/t/${code}/app-flow`} exportPath={`/t/${code}/app-flow/export.pptx`} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
